import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ModalUI, Switch, showNotification, Tabs, type TabsItemProps } from 'sonata-react-components';

import { CsvDropzone } from '@/components/common/CsvDropzone/CsvDropzone';
import { POIImporterResultList } from '@/components/common/MultiFlightMap/components/AttributionPOI/POIImporterResultList/POIImporterResultList';
import { POIImporter } from '@/components/common/POIImporter/POIImporter';
import { type ImportMode, usePOIImporter } from '@/components/common/POIImporter/POIImporter.context';
import { useMultiFlightMap } from '../../../MultiFlightMap.context';
import { CountDown } from '../CountDown/CountDown';
import { POIInventory } from '../POIInventory/POIInventory';

import type { FileData } from '@/components/common/POIImporter/POIImporter.hooks';

import './attributionPOIModal.scss';

type State = {
  countdownShown: boolean;
};

export function AttributionPOIModal() {
  const { formatMessage } = useIntl();
  const { entityId, entityType } = useMultiFlightMap();
  const modalRef = useRef<React.ElementRef<typeof ModalUI>>(null);
  const {
    attributionMode,
    cancelUploadProcess,
    file,
    processBatchUploadFile,
    processingStatus,
    importMode,
    setShowModal,
    setImportMode,
    venuesAdded,
    setFile
  } = usePOIImporter();

  const isProximityPointsMode = attributionMode === 'proximityPoints';

  const [{ countdownShown }, setState] = useState<State>({
    countdownShown: false
  });

  useEffect(() => {
    modalRef.current?.openModal?.();
  }, [modalRef.current]);

  const handleCancelProcess = () => {
    cancelUploadProcess('attributionPOIUpload');
    handleCloseModal();
  };

  const handleCloseModal = () => {
    setState(prevState => ({
      ...prevState,
      countdownShown: false
    }));
    setShowModal(false);
  };

  const handleAddComplete = () => {
    setState(prevState => ({
      ...prevState,
      countdownShown: false
    }));
    setFile(null);
  };

  const handleApplyButtonClicked = async (fileData: FileData): Promise<boolean> => {
    const success = await processBatchUploadFile(fileData, 'attributionPOIUpload', !isProximityPointsMode);
    if (success && fileData.exceedsMaxRows) {
      handleCloseModal();
      showNotification(
        'success',
        formatMessage({
          defaultMessage: 'Your file has been sent to our support team successfully.',
          id: 'attributionPOI.notification.forwardedToSupport'
        })
      );
      return success;
    }
    if (success) {
      setState(prevState => ({
        ...prevState,
        countdownShown: true
      }));
    }
    return success;
  };

  const templateURL = isProximityPointsMode
    ? `${taptap.config.sonataStaticsUrlBase}/samples/proximityPointsModal/Proximity_Points_template.csv`
    : `${taptap.config.sonataStaticsUrlBase}/samples/attributionPOIUpload/Attribution_POI_template.csv`;

  const tabs: TabsItemProps[] = [
    {
      component: <POIInventory />,
      icon: {
        content: isProximityPointsMode ? 'fa-solid fa-thumbtack' : 'fa-kit-duotone fa-poi-inventory',
        position: 'left'
      },
      id: 'inventory',
      title: isProximityPointsMode
        ? formatMessage({
            defaultMessage: 'Coordinates Inventory',
            id: 'attributionPOI.modal.tabs.coordinatesInventory'
          })
        : formatMessage({
            defaultMessage: 'POI Inventory',
            id: 'attributionPOI.modal.tabs.inventory'
          })
    },
    {
      component: (
        <main className="attributionPOIUpload" data-testid="attribution-poi-upload-tab">
          <CsvDropzone
            inputId="attribution-poi-csv"
            onFile={setFile}
            templateLabel={formatMessage(
              {
                defaultMessage: 'CSV Template (1kb, maxSize: {maxSize})',
                id: 'attributionPOI.modal.dropzone.templateDownload'
              },
              {
                maxSize: '12Mb'
              }
            )}
            // No dedicated CSV template exists yet for Proximity Points — reusing the
            // Attribution POI template, which has the same required columns (name, lat/lng).
            templateUrl={templateURL}
            uploadLabel={formatMessage({
              defaultMessage: 'Upload data file',
              id: 'attributionPOI.modal.dropzone.uploadButton'
            })}
            warningLabel={
              isProximityPointsMode
                ? formatMessage({
                    defaultMessage: 'Upload limit: 3,000 points to prevent oversaturation',
                    id: 'attributionPOI.modal.dropzone.oversaturationWarning.points'
                  })
                : formatMessage({
                    defaultMessage: 'Upload limit: 3,000 POIs to prevent oversaturation',
                    id: 'attributionPOI.modal.dropzone.oversaturationWarning'
                  })
            }
          />
        </main>
      ),
      icon: {
        content: isProximityPointsMode ? 'fa-kit fa-solid-thumbtack-upload' : 'fa-regular fa-arrow-up-to-line',
        position: 'left'
      },
      id: 'upload',
      title: isProximityPointsMode
        ? formatMessage({
            defaultMessage: 'Upload Your Coordinates',
            id: 'attributionPOI.upload.coordinates.title'
          })
        : formatMessage({
            defaultMessage: 'Upload your POIs',
            id: 'attributionPOI.upload.title'
          })
    }
  ];

  const modalDescription = isProximityPointsMode
    ? importMode === 'inventory'
      ? formatMessage({
          defaultMessage:
            'Select coordinates you have already added, including custom lat/lng ones that may not exist on Google Maps, to measure their footfall attribution.',
          id: 'attributionPOI.selectType.points.inventory.description'
        })
      : formatMessage({
          defaultMessage:
            'Upload your custom lat/lng coordinates that may not exist on Google Maps, to measure their footfall attribution.',
          id: 'attributionPOI.selectType.points.upload.description'
        })
    : importMode === 'inventory'
      ? formatMessage({
          defaultMessage:
            'You can integrate pre-existing Points of Interest compiled from various public and commercial providers, and measure their  attribution.',
          id: 'attributionPOI.selectType.inventory.description'
        })
      : formatMessage({
          defaultMessage:
            'Upload your own POIs. Note that our system only supports businesses with high public footfall or significant engagement as POIs.',
          id: 'attributionPOI.selectType.upload.description'
        });

  const modalTitle = isProximityPointsMode
    ? importMode === 'inventory'
      ? formatMessage({
          defaultMessage: 'My Coordinates Inventory',
          id: 'attributionPOI.selectType.coordinates.inventory.title'
        })
      : formatMessage({
          defaultMessage: 'Upload Your Coordinates',
          id: 'attributionPOI.selectType.coordinates.upload.title'
        })
    : importMode === 'inventory'
      ? formatMessage({
          defaultMessage: 'POI Inventory',
          id: 'attributionPOI.selectType.inventory.title'
        })
      : formatMessage({
          defaultMessage: 'Upload your POIs',
          id: 'attributionPOI.selectType.upload.title'
        });

  return (
    <ModalUI
      closeModalCallback={() => setShowModal(false)}
      description={modalDescription}
      ref={modalRef}
      title={modalTitle}
      width="1240px"
    >
      <Switch>
        <Switch.Case condition={Boolean(file) && processingStatus === 'success' && venuesAdded.length > 0}>
          <POIImporterResultList
            enablePoiFinder={!isProximityPointsMode}
            onAddComplete={handleAddComplete}
            onCancelProcess={handleCancelProcess}
            onGoBack={() => setFile(null)}
          />
        </Switch.Case>
        <Switch.Case condition={Boolean(file) && processingStatus === 'processing'}>
          <CountDown
            countdownShown={countdownShown}
            onCancel={handleCancelProcess}
            onFinish={handleCloseModal}
            onMinimize={handleCloseModal}
          />
        </Switch.Case>
        <Switch.Case condition={Boolean(file)}>
          <POIImporter
            enablePoiFinder={!isProximityPointsMode}
            entity={{
              id: entityId,
              type: entityType
            }}
            file={file!}
            onApply={handleApplyButtonClicked}
            onFileChange={cleanedFile => setFile(cleanedFile, true)}
            onFinish={() => setShowModal(false)}
            onGoBack={() => setFile(null)}
          />
        </Switch.Case>
        <Switch.Default>
          <Tabs
            className="attributionPOIUpload__tabs"
            hideNoneActivePanels
            initialTab={importMode as string}
            onChangeTab={tabId => setImportMode(tabId as ImportMode)}
            shape="default"
            tabs={tabs}
            variant="filled"
          />
        </Switch.Default>
      </Switch>
    </ModalUI>
  );
}
